import React from "react";
import { Outlet, Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { IdCard, LayoutDashboard, Layers, Library as LibIcon, CreditCard, LogOut, Plus, Shield } from "lucide-react";

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-3 font-mono text-xs uppercase tracking-wider font-bold border-l-4 ${
    isActive ? "bg-black text-white border-[#002FA7]" : "text-black border-transparent hover:bg-gray-100"
  }`;

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#F3F4F6]">
      {/* TOP NAV */}
      <nav className="brutal-border border-t-0 border-l-0 border-r-0 bg-white sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="app-logo">
            <div className="w-9 h-9 bg-black flex items-center justify-center"><IdCard className="w-5 h-5 text-white" strokeWidth={2.5}/></div>
            <span className="font-display font-black text-xl tracking-tighter">DIGITAL CARD</span>
          </Link>
          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-2 bg-black text-white px-3 py-2" data-testid="credits-badge">
              <CreditCard className="w-4 h-4"/>
              <span className="font-mono text-xs uppercase tracking-widest">Credits:</span>
              <span className="font-mono text-sm font-bold">{user?.credits ?? 0}</span>
            </div>
            <Link to="/billing" className="hidden md:inline-flex items-center gap-1 bg-[#22C55E] text-black px-3 py-2 brutal-border brutal-shadow-sm font-mono text-xs uppercase tracking-widest font-bold hover:bg-[#16A34A]" data-testid="buy-credits-btn">
              <Plus className="w-4 h-4"/> Buy
            </Link>
            <div className="hidden md:block font-mono text-xs uppercase tracking-widest text-gray-600" data-testid="user-name">{user?.name || user?.email}</div>
            <button onClick={onLogout} className="flex items-center gap-2 px-3 py-2 brutal-border brutal-shadow-sm font-mono text-xs uppercase tracking-widest font-bold hover:bg-gray-100" data-testid="logout-btn">
              <LogOut className="w-4 h-4"/> Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-[1400px] mx-auto grid md:grid-cols-[240px_1fr] min-h-[calc(100vh-64px)]">
        {/* SIDEBAR */}
        <aside className="border-r-2 border-black bg-white">
          <div className="p-4 border-b-2 border-black">
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">// Menu</div>
          </div>
          <nav className="py-2">
            <NavLink to="/dashboard" className={linkCls} data-testid="nav-dashboard"><LayoutDashboard className="w-4 h-4"/> Dashboard</NavLink>
            <NavLink to="/templates" className={linkCls} data-testid="nav-templates"><Layers className="w-4 h-4"/> Templates</NavLink>
            <NavLink to="/library" className={linkCls} data-testid="nav-library"><LibIcon className="w-4 h-4"/> Library</NavLink>
            <NavLink to="/billing" className={linkCls} data-testid="nav-billing"><CreditCard className="w-4 h-4"/> Credits</NavLink>
            {user?.is_admin && (
              <NavLink to="/admin" className={linkCls} data-testid="nav-admin"><Shield className="w-4 h-4"/> Admin</NavLink>
            )}
          </nav>
          <div className="p-4 mt-4 mx-4 bg-[#002FA7] text-white brutal-border brutal-shadow-sm">
            <div className="font-mono text-[10px] uppercase tracking-widest opacity-80 mb-1">Free tier</div>
            <div className="font-display font-black text-lg leading-tight">{user?.free_used ? "Used" : "1 free card"}</div>
            <div className="text-xs mt-1 opacity-90">Then ₹10 / card</div>
          </div>
        </aside>

        {/* CONTENT */}
        <main className="p-6 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
